import {useCallback} from 'react';

import {useDatadogContext} from './DatadogContext';

/**
 * React hook that returns a callback to log feature flag evaluations to Datadog.
 * The callback logs the feature flag to both RUM and Logs with the repaired flag name.
 *
 * @param key - The feature flag key
 * @returns A callback function to log the feature flag value
 */
export function useDatadogFeatureFlag(key: string) {
  const {rum: datadogRum, logs: datadogLogs} = useDatadogContext();

  const flagName = repairFeatureFlagName(key);
  return useCallback(
    (value: unknown) => {
      datadogRum.addFeatureFlagEvaluation(flagName, value);
      datadogLogs.setUserProperty(`features.${flagName}`, value);
    },
    [datadogLogs, datadogRum, flagName]
  );
}

/**
 * Datadog doesn't like feature flags with `.` in the name. If you log a
 * feature with `.` in the name, it gets rolled up with all the other flags
 * under the same prefix, and sets them all to the string `object Object`. To
 * avoid that, we fixup the feature flag name so that DD doesn't interpret it
 * as an object literal path.
 *
 * @param name - The feature flag name to repair
 * @returns The repaired feature flag name with dots replaced by double underscores
 */
function repairFeatureFlagName(name: string): string {
  return name.replace(/\./g, '__');
}
